import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import MainLayout from './layouts/MainLayout';
import {
  Dashboard,
  Analytics,
  Tasks,
  Routes as RoutesPage,
  RouteEditor,
  Zones,
  Drivers,
  Schedules,
  LiveMonitor,
  Settings,
  MerchantPickupWindows,
  MerchantPickupLocations,
  CarrierCutoffs,
  PickupSlots,
  SlotSettings,
} from './pages';
import RouteConfig from './pages/RouteConfig';
import RoutePreview from './pages/RoutePreview';
import ApiLogs from './pages/ApiLogs';
import TaskLookup from './pages/TaskLookup';
import AuditLogs from './pages/AuditLogs';
import Simulation from './pages/Simulation';
import DriverMonitor from './pages/DriverMonitor';
import WorkingHours from './pages/WorkingHours';
import ShadowPlanPage from './pages/ShadowPlan';
import Login from './pages/Login';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { logger } from './utils/logger';

// Create a client with logging
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => {
        logger.error('Mutation error', error);
      },
    },
  },
});

// Log query errors globally
queryClient.getQueryCache().subscribe((event) => {
  if (event.type === 'updated' && event.query.state.status === 'error') {
    logger.error('Query error', event.query.state.error, {
      queryKey: JSON.stringify(event.query.queryKey),
    });
  }
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public route */}
            <Route path="/login" element={<Login />} />
            
            {/* Protected routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }>
              <Route index element={<ProtectedRoute permission="dashboard.view"><Dashboard /></ProtectedRoute>} />
              <Route path="analytics" element={<ProtectedRoute permission="analytics.view"><Analytics /></ProtectedRoute>} />
              <Route path="tasks" element={<ProtectedRoute permission="tasks.view"><Tasks /></ProtectedRoute>} />
              <Route path="routes" element={<ProtectedRoute permission="routes.view"><RoutesPage /></ProtectedRoute>} />
              <Route path="routes/configure" element={<ProtectedRoute permission="routes.generate"><RouteConfig /></ProtectedRoute>} />
              <Route path="routes/preview" element={<ProtectedRoute permission="routes.generate"><RoutePreview /></ProtectedRoute>} />
              <Route path="routes/edit/:runId" element={<ProtectedRoute permission="routes.edit"><RouteEditor /></ProtectedRoute>} />
              <Route path="shadow-plan" element={<ProtectedRoute permission="shadow.view"><ShadowPlanPage /></ProtectedRoute>} />
              <Route path="zones" element={<ProtectedRoute permission="zones.view"><Zones /></ProtectedRoute>} />
              <Route path="drivers" element={<ProtectedRoute permission="drivers.view"><Drivers /></ProtectedRoute>} />
              <Route path="schedules" element={<ProtectedRoute permission="schedules.view"><Schedules /></ProtectedRoute>} />
              <Route path="monitor" element={<ProtectedRoute permission="routes.view"><LiveMonitor /></ProtectedRoute>} />
              <Route path="driver-monitor" element={<ProtectedRoute permission="drivers.view"><DriverMonitor /></ProtectedRoute>} />
              <Route path="working-hours" element={<ProtectedRoute permission="working_hours.view"><WorkingHours /></ProtectedRoute>} />
              <Route path="settings" element={<Settings />} />
              <Route path="logs" element={<ProtectedRoute permission="logs.view"><ApiLogs /></ProtectedRoute>} />
              <Route path="task-lookup" element={<ProtectedRoute permission="tasks.view"><TaskLookup /></ProtectedRoute>} />
              <Route path="audit-logs" element={<ProtectedRoute permission="audit.view"><AuditLogs /></ProtectedRoute>} />
              <Route path="merchants" element={<ProtectedRoute permission="merchants.view"><MerchantPickupWindows /></ProtectedRoute>} />
              <Route path="merchant-locations" element={<ProtectedRoute permission="merchants.view"><MerchantPickupLocations /></ProtectedRoute>} />
              <Route path="carrier-cutoffs" element={<ProtectedRoute permission="cutoffs.view"><CarrierCutoffs /></ProtectedRoute>} />
              <Route path="pickup-slots" element={<ProtectedRoute permission="slots.view"><PickupSlots /></ProtectedRoute>} />
              <Route path="slot-settings" element={<ProtectedRoute permission="slots.view"><SlotSettings /></ProtectedRoute>} />
              <Route path="simulation" element={<ProtectedRoute permission="simulation.view"><Simulation /></ProtectedRoute>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
